import { ProductPageHeader } from '@design-system/product-page-header';
import {
  getLocalStorageData,
  removeLocalStorageData,
} from '@phx-husky/local-storage';
import { ScreenRecognitionContext } from '@phx-husky/screen-recognition';
import { graphql } from 'gatsby';
import { kebabCase } from 'lodash';
import React, { FC, useContext, useEffect } from 'react';

import Adimo from '~/common/Adimo';
import { checkAdimoAvailable } from '~/common/Adimo/utils';
import BazaarVoice from '~/common/BazaarVoice';
import Mikmak from '~/common/MikMak';
import { checkMikMakAvailable } from '~/common/MikMak/utils';
import PriceSpider from '~/common/PriceSpider';
import { PriceSpiderModel } from '~/common/PriceSpider/models';
import { checkPriceSpiderAvailable } from '~/common/PriceSpider/utils';
import BodyRender from '~/components/BodyRender';
import { BodyRenderExternalData } from '~/components/BodyRender/models';
import ItemListing from '~/components/ItemListing';
import { ItemListingCarouselProps } from '~/components/ItemListing/models';
import LoginRegisterBlocks from '~/components/LoginRegisterBlocks';
import { PAGE_ONLY_FOR_SIGNED_IN } from '~/components/LoginRegisterBlocks/constants';
import { mapContentstackTranslations } from '~/context/TranslationContext/mapper';
import { useAuthGuard } from '~/hooks/useAuthGuard';
import { Layout } from '~/layout/Layout';
import { itemListingMapper } from '~/mappers/itemListing';
import { productPageHeaderMapper } from '~/mappers/productPageHeader';
import {
  createItemListingCarouselMock,
  mapPageDataToCarouselItem,
  mapProductPageDataToCarouselItem,
  PAGE_TYPE,
} from '~/mocks/itemListingCaorusel';
import { gtmService } from '~/services/gtm';
import { GTM_LOCALSTORAGE_DATA } from '~/utils/constants';
import { filterListingItemsByBrand } from '~/utils/filterListingItems';
import withContentstackPreview from '~/utils/livePreview';

import localeConfig from '../../localeConfig';
import { ProductPageData } from './models';

const BASE_CLASS = 'l2-product-page';

// eslint-disable-next-line max-statements
const ProductPage: FC<ProductPageData> = ({
  data: {
    profile,
    contentstackL2ProductPage,
    contentstackHeader,
    allContentstackL2ProductPage,
    allContentstackL2ResourcePage,
    contentstackExtendedHeader,
    contentstackTranslations,
    contentstackHeaderWithSplitter,
    contentstackNewFooter,
    contentstackFooterWithSplitter,
    contentstackInfoBar,
    contentstackSettings,
    allContentstackAssets: { nodes: jsonRTEImages },
  },
  pageContext: { uid, locale, seoTitle, brand, pageTemplate, fullPath },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const { isMobile } = useContext(ScreenRecognitionContext);

  const t = mapContentstackTranslations(contentstackTranslations);

  const {
    seo,
    body,
    fields: { productData },
    categories,
    openGraph,
    productBrand,
  } = contentstackL2ProductPage;
  const {
    bazaarvoiceUrl,
    siteUrl,
    bazaarvoiceProductFieldName,
    priceSpider,
    adimo,
    shoppablePlugin,
    pluginInformationText,
    mikmak,
  } = contentstackSettings;

  const isPriceSpiderAvailable = checkPriceSpiderAvailable(
    shoppablePlugin,
    priceSpider
  );
  const isAdimoAvailable = checkAdimoAvailable(shoppablePlugin, adimo);
  const mainImagePath = productData.heroImage.url;
  const isMikMakAvailable = checkMikMakAvailable(shoppablePlugin, mikmak);

  const lastProductIndex = getLocalStorageData(
    GTM_LOCALSTORAGE_DATA.lastProductIndex
  );
  gtmService.emitViewItem(productData, pageTemplate, lastProductIndex);

  useEffect(() => {
    const handleBeforeUnload = () => {
      removeLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      removeLocalStorageData(GTM_LOCALSTORAGE_DATA.lastProductIndex);
    };
  }, []);

  const onlyForSignedIn =
    process.env.GATSBY_PROTECTED_PAGES_ENABLED === 'true' &&
    process.env.GATSBY_PROTECTED_PRODUCT_PAGES === 'true';

  const { isLoggedIn } = useAuthGuard(onlyForSignedIn);

  const isAuthPending = onlyForSignedIn && isLoggedIn === null;
  const isAccessDenied = onlyForSignedIn && !isLoggedIn;

  if (isAuthPending) return null;

  if (isAccessDenied) {
    return <LoginRegisterBlocks variant={PAGE_ONLY_FOR_SIGNED_IN} />;
  }

  const categoryClasses = categories.map(
    ({ title }) => `${BASE_CLASS}--${kebabCase(title)}`
  );

  const externalData: BodyRenderExternalData = {
    salsifyExternalData: productData,
    jsonRTEImages,
  };

  const productPageHeaderData = productData
    ? productPageHeaderMapper({
        productPageHeaderBasic: {
          ...contentstackL2ProductPage,
          productId: productData[bazaarvoiceProductFieldName],
          isPriceSpiderAvailable,
          isAdimoAvailable,
          adimoKey: adimo?.key,
          adimoButton: adimo?.buttonText,
          isMikMakAvailable,
          mikmakAppId: mikmak?.appId,
          mikmakButton: mikmak?.buttonText,
          pluginInformationText,
        },
      })
    : null;

  const relatedProductPages = allContentstackL2ProductPage?.nodes || [];

  const filteredRelatedProductListingItems = filterListingItemsByBrand(
    relatedProductPages,
    productBrand?.[0]?.brandName,
    12
  );

  const hasRelatedProductPages =
    relatedProductPages.length > 0 &&
    filteredRelatedProductListingItems.length > 1;

  let relatedProductsMappedData: ItemListingCarouselProps | null = null;

  if (hasRelatedProductPages) {
    const mockItemListingProductsCarousel = createItemListingCarouselMock(
      isMobile,
      t,
      PAGE_TYPE.PRODUCTS,
      productBrand
    );

    if (filteredRelatedProductListingItems.length === 0) {
      mockItemListingProductsCarousel.items[0].items = relatedProductPages
        .map(mapProductPageDataToCarouselItem)
        .slice(0, 12);
    } else {
      mockItemListingProductsCarousel.items[0].items =
        filteredRelatedProductListingItems.map(
          mapProductPageDataToCarouselItem
        );
    }

    relatedProductsMappedData = itemListingMapper(
      mockItemListingProductsCarousel,
      uid
    ) as ItemListingCarouselProps;
  }

  const relatedResourcesPages = allContentstackL2ResourcePage?.nodes || [];

  const hasRelatedResourcesPages = relatedResourcesPages.length > 0;

  let relatedResourcesMappedData: ItemListingCarouselProps | null = null;

  if (hasRelatedResourcesPages) {
    const mockItemListingResourcesCarousel = createItemListingCarouselMock(
      isMobile,
      t,
      PAGE_TYPE.RESOURCES
    );

    mockItemListingResourcesCarousel.items[0].items = relatedResourcesPages.map(
      mapPageDataToCarouselItem
    );

    relatedResourcesMappedData = itemListingMapper(
      mockItemListingResourcesCarousel,
      uid
    ) as ItemListingCarouselProps;
  }

  const blocksMap = {
    products:
      hasRelatedProductPages && relatedProductsMappedData ? (
        <ItemListing {...relatedProductsMappedData} />
      ) : null,
    resources:
      hasRelatedResourcesPages && relatedResourcesMappedData ? (
        <ItemListing {...relatedResourcesMappedData} />
      ) : null,
  };

  return (
    <Layout
      {...{
        openGraph,
        mainImagePath,
        extendedHeaderBasic: contentstackExtendedHeader,
        translations: t,
        seo: {
          ...seo,
          additionalMeta: [...seo.additionalMeta],
          pageType: 'og:product',
        },
        headerBasic: contentstackHeader,
        headerWithSplitter: contentstackHeaderWithSplitter,
        headerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        footerBasic: contentstackNewFooter,
        footerWithSplitter: contentstackFooterWithSplitter,
        footerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        pageUid: uid,
        locale,
        brand,
        className: [BASE_CLASS, ...categoryClasses].join(' '),
        infoBar: {
          ...contentstackInfoBar,
        },
        fullPath,
        profile,
      }}
    >
      {bazaarvoiceUrl ? (
        <BazaarVoice
          url={bazaarvoiceUrl}
          productData={productData}
          productId={productData[bazaarvoiceProductFieldName]}
          brand={brand}
          siteUrl={siteUrl}
          lang={locale}
        />
      ) : null}
      {isPriceSpiderAvailable ? (
        <PriceSpider
          priceSpider={priceSpider as PriceSpiderModel}
          lang={locale}
        />
      ) : null}
      {isAdimoAvailable ? <Adimo url={adimo!.url!} /> : null}
      {isMikMakAvailable ? <Mikmak appId={mikmak?.appId!} /> : null}
      {productPageHeaderData ? (
        <ProductPageHeader {...productPageHeaderData} />
      ) : null}
      <BodyRender
        items={body}
        externalData={externalData}
        currentPageUid={uid}
      />
      {localeConfig.order.map((key) => blocksMap[key])}
    </Layout>
  );
};

export const query = graphql`
  query ProductPage(
    $id: String
    $locale: String
    $brand: [String]
    $jsonRTEImgUids: [String]
    $categoryUids: [String]
  ) {
    profile: contentstackProfile {
      ...ProfileFragment
    }
    contentstackL2ProductPage(
      id: { eq: $id }
      publish_details: { locale: { eq: $locale } }
    ) {
      updated_at
      publish_details {
        time
      }
      seo {
        ...SeoFragment
      }
      openGraph: open_graph {
        ...OpenGraphFragment
      }
      body {
        ...BodyFragment
      }
      ...L2ProductPageFragment
      variantSelector: variant_selector {
        variants {
          ...L2ProductPageFragment
          ...L2ProductPageCutOutSplitterFragment
        }
      }
      categories {
        title
      }
    }
    allContentstackAssets(filter: { uid: { in: $jsonRTEImgUids } }) {
      nodes {
        uid
        url
        gatsbyImageData
      }
    }
    allContentstackL2ProductPage(
      filter: { categories: { elemMatch: { uid: { in: $categoryUids } } } }
    ) {
      nodes {
        uid
        updated_at
        fields {
          fullPath
        }
        productBrand: product_brand {
          ...BrandFragment
        }
        publishDetails: publish_details {
          locale
        }
        categories {
          title
          uid
          use_in_lables
        }
        listing_data {
          description
          label {
            content
            size
            sr_prefix
            variant
          }
        }
        listing_image {
          image {
            url
            gatsbyImageData(width: 550)
          }
          rounded
          object_position
          object_fit
          caption
          aspect_ratio
          alt
        }
        local_pim_data {
          hero_image {
            url
            gatsbyImageData(width: 550, aspectRatio: 1.5)
            title
          }
          description_short
          product_title
          aroma
          description_long
          sku
          barcode_number
          size
          product_features {
            alt
            description
          }
        }
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderBasicFragment
    }
    contentstackTranslations(publish_details: { locale: { eq: $locale } }) {
      ...Translations
    }
    contentstackExtendedHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...ExtendedHeaderBasicFragment
    }
    contentstackHeaderWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderWithSplitterFragment
    }
    contentstackSettings(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...SettingsFragment
    }
    contentstackInfoBar(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...InfoBarFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterBasicFragment
    }
    contentstackNewFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...NewFooterBasicFragment
    }
    contentstackFooterWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterWithSplitterFragment
    }
    allContentstackL2ResourcePage(
      filter: { categories: { elemMatch: { uid: { in: $categoryUids } } } }
      sort: {
        fields: [page_additional_data___initial_publish_date]
        order: DESC
      }
      limit: 12
    ) {
      nodes {
        uid
        updated_at
        fields {
          fullPath
        }
        pageHeader: page_header {
          headingBlock: heading_block {
            headline
            descriptionRte: description_rte
          }
          image_element {
            image {
              url
              gatsbyImageData(width: 550)
            }
            alt
            aspectRatio: aspect_ratio
            objectFit: object_fit
            objectPosition: object_position
            rounded
            caption
          }
        }
        publishDetails: publish_details {
          locale
        }
        categories {
          title
          uid
          use_in_lables
        }
        use_fallback_image
        listing_image {
          image {
            url
            gatsbyImageData(width: 550)
          }
          alt
          aspectRatio: aspect_ratio
          objectFit: object_fit
          objectPosition: object_position
          rounded
          caption
        }
        page_additional_data {
          duration
          initial_publish_date
          resource_link
          content_type
        }
        listing_data {
          description
          label {
            content
            sr_prefix
            variant
            size
          }
        }
      }
    }
  }
`;

export default withContentstackPreview(ProductPage, 'productpage');

export { Head } from '~/common/StructuredData';
